/**
 * Sales Conversion report — per sales user: CRM hot-conversion leads vs
 * enrolments vs revenue, shown with BOTH attributions side by side:
 *  (a) Lead-linked (invoice cf_enrolment_no ← lead Enrolment No, attributed to hot-conversion month)
 *  (b) Books salesperson (invoice date in range, salesperson ≈ owner)
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import GlobalSyncPill from './GlobalSyncPill';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import api from '../../lib/api';
import { toast } from 'sonner';
import { RefreshCw, Loader2, Users, GraduationCap, IndianRupee, FileText, ExternalLink } from 'lucide-react';
import { SpFilter } from '../../components/books/FilterKit';

const fmtInr = (n) => (n == null || isNaN(n)) ? '—' : `₹${Math.round(n).toLocaleString('en-IN')}`;
const ZOHO_ORG = '699857543';
const zohoInvUrl = (id) => `https://books.zoho.com/app/${ZOHO_ORG}/#/invoices/${id}`;
const crmLeadUrl = (id) => `https://crm.zoho.com/crm/org690252559/tab/Leads/${id}`;

const STATUS_COLORS = {
  'Admission': 'bg-emerald-100 text-emerald-700',
  'Closed Won Registered': 'bg-emerald-100 text-emerald-700',
  'Closed Lost': 'bg-rose-100 text-rose-600',
  'Walked In Closed Lost': 'bg-rose-100 text-rose-600',
  'Junk': 'bg-slate-200 text-slate-600',
  'Cold': 'bg-sky-100 text-sky-700',
};

const EXCLUDED_STATUSES = ['Duplicate Lead', 'Global Certification', 'Junk', 'Location Issue', 'Other Course', 'Unable to Reach'];

function computeRange(preset, monthKey) {
  const t = new Date(); const y = t.getFullYear(), m = t.getMonth();
  const fyStartYear = m >= 3 ? y : y - 1;
  const iso = (dt) => new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  if (preset === 'this_month') return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)), label: t.toLocaleString('en-IN', { month: 'short', year: 'numeric' }) };
  if (preset === 'last_month') return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)), label: 'Last month' };
  if (preset === 'this_fy') return { from: `${fyStartYear}-04-01`, to: `${fyStartYear + 1}-03-31`, label: `FY ${fyStartYear}-${String(fyStartYear + 1).slice(2)}` };
  if (preset === 'month' && monthKey) {
    const [yy, mm] = monthKey.split('-').map(Number);
    return { from: iso(new Date(yy, mm - 1, 1)), to: iso(new Date(yy, mm, 0)), label: new Date(yy, mm - 1, 1).toLocaleString('en-IN', { month: 'short', year: 'numeric' }) };
  }
  return { from: `${fyStartYear}-04-01`, to: iso(t), label: 'YTD' };
}

function buildMonthOptions() {
  const out = []; const now = new Date();
  let d = new Date(2025, 3, 1);
  while (d <= now) {
    out.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleString('en-IN', { month: 'short', year: 'numeric' }) });
    d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  }
  return out.reverse();
}

export default function SalesConversionPage() {
  const [preset, setPreset] = useState('this_month');
  const [monthKey, setMonthKey] = useState(() => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}`; });
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [owner, setOwner] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sync, setSync] = useState({ running: false, done: 0, total_chunks: 0, leads: 0, last_sync: null, lead_count: 0 });
  const [drill, setDrill] = useState({ open: false, title: '', leads: [], invoices: [], tab: 'leads' });
  const [excludeNonConnect, setExcludeNonConnect] = useState(true);
  const [spState, setSpState] = useState({ op: 'is in', selected: [] });
  const [ctState, setCtState] = useState({ op: 'is in', selected: [] });
  const [perfView, setPerfView] = useState('user');
  const monthOpts = useMemo(buildMonthOptions, []);

  const range = useMemo(() => preset === 'custom' ? { from: customFrom, to: customTo, label: 'Custom' } : computeRange(preset, monthKey), [preset, monthKey, customFrom, customTo]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (range.from) params.from_date = range.from;
      if (range.to) params.to_date = range.to;
      if (owner) params.owner = owner;
      const r = await api.get('/sales-conversion/report', { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally { setLoading(false); }
  }, [range.from, range.to, owner]);

  const fetchSyncStatus = useCallback(async () => {
    try { const r = await api.get('/sales-conversion/sync-status'); setSync(r.data); return r.data; } catch { return null; }
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);
  useEffect(() => { fetchSyncStatus(); }, [fetchSyncStatus]);

  useEffect(() => {
    if (!sync.running) return;
    const id = setInterval(async () => {
      const s = await fetchSyncStatus();
      if (s && !s.running) {
        clearInterval(id);
        if (s.error) toast.error(`Sync failed: ${s.error}`);
        else toast.success(`Lead sync complete — ${s.leads} leads updated`);
        fetchReport();
      }
    }, 3000);
    return () => clearInterval(id);
  }, [sync.running, fetchSyncStatus, fetchReport]);

  const startSync = async (allHistory = false) => {
    try {
      const params = {};
      if (allHistory) {
        params.from_date = '2022-04-01';
        params.to_date = new Date().toISOString().slice(0, 10);
      } else {
        if (range.from) params.from_date = range.from;
        if (range.to) params.to_date = range.to;
      }
      const r = await api.post('/sales-conversion/sync', null, { params });
      if (r.data.started) { toast.info(`Syncing hot-conversion leads ${r.data.from_date} → ${r.data.to_date}${allHistory ? ' (full history — takes a few minutes)' : ''}…`); setSync((s) => ({ ...s, running: true, done: 0, leads: 0 })); }
      else toast.info(r.data.message || 'Sync already running');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start sync'); }
  };

  const s = data?.summary;

  const effectiveLeads = useMemo(() => {
    if (!data) return [];
    return excludeNonConnect ? data.leads.filter((l) => !EXCLUDED_STATUSES.includes(l.status)) : data.leads;
  }, [data, excludeNonConnect]);

  const effectiveLinkedInvoices = useMemo(() => data ? data.linked_invoices : [], [data]);

  const passOp = (val, st) => st.selected.length === 0 || (st.op === 'is in' ? st.selected.includes(val) : !st.selected.includes(val));

  const filteredLeads = useMemo(() => effectiveLeads.filter((l) => passOp(l.owner_name || '(none)', spState)), [effectiveLeads, spState]);
  const filteredInvoices = useMemo(
    () => effectiveLinkedInvoices.filter((i) => passOp(i.owner_name || '(none)', spState) && passOp(i.client_type || 'Not set', ctState)),
    [effectiveLinkedInvoices, spState, ctState],
  );
  const filteredBooksInvoices = useMemo(
    () => (data?.books_invoices || []).filter((i) => passOp(i.client_type || 'Not set', ctState)),
    [data, ctState],
  );

  const allOwnerNames = useMemo(() => {
    const s = new Set((data?.owners || []).map((o) => o.name));
    for (const i of (data?.linked_invoices || [])) s.add(i.owner_name || '(none)');
    return [...s].sort();
  }, [data]);
  const allClientTypes = useMemo(() => {
    const s = new Set();
    for (const i of (data?.linked_invoices || [])) s.add(i.client_type || 'Not set');
    for (const i of (data?.books_invoices || [])) s.add(i.client_type || 'Not set');
    return [...s].sort();
  }, [data]);

  const ownerLeads = useMemo(() => owner ? filteredLeads.filter((l) => l.owner_name === owner) : filteredLeads, [filteredLeads, owner]);
  const ownerLinkedInvoices = useMemo(() => owner ? filteredInvoices.filter((i) => i.owner_name === owner) : filteredInvoices, [filteredInvoices, owner]);

  const perfRows = useMemo(() => {
    if (!data) return [];
    const m = {};
    for (const l of filteredLeads) {
      const o = l.owner_name || '(none)';
      const e = m[o] || (m[o] = { owner: o, leads: 0, enrolSet: new Set(), inv: 0, rev: 0 });
      e.leads += 1;
    }
    for (const i of filteredInvoices) {
      const o = i.owner_name || '(none)';
      const e = m[o] || (m[o] = { owner: o, leads: 0, enrolSet: new Set(), inv: 0, rev: 0 });
      e.enrolSet.add(i.enrolment_no);
      e.inv += 1; e.rev += i.total;
    }
    const totalRev = Object.values(m).reduce((a, r) => a + r.rev, 0);
    return Object.values(m)
      .map((r) => ({ ...r, enrols: r.enrolSet.size, convPct: r.leads ? (r.enrolSet.size / r.leads) * 100 : 0, salesPct: totalRev ? (r.rev / totalRev) * 100 : 0 }))
      .sort((a, b) => b.rev - a.rev || b.leads - a.leads);
  }, [data, filteredLeads, filteredInvoices]);

  const perfCatRows = useMemo(() => {
    if (!data) return [];
    const m = {};
    for (const l of filteredLeads) {
      const g = l.group || '(none)';
      const e = m[g] || (m[g] = { owner: g, leads: 0, enrolSet: new Set(), inv: 0, rev: 0 });
      e.leads += 1;
    }
    for (const i of filteredInvoices) {
      const g = i.group || '(none)';
      const e = m[g] || (m[g] = { owner: g, leads: 0, enrolSet: new Set(), inv: 0, rev: 0 });
      e.enrolSet.add(i.enrolment_no);
      e.inv += 1; e.rev += i.total;
    }
    const totalRev = Object.values(m).reduce((a, r) => a + r.rev, 0);
    return Object.values(m)
      .map((r) => ({ ...r, enrols: r.enrolSet.size, convPct: r.leads ? (r.enrolSet.size / r.leads) * 100 : 0, salesPct: totalRev ? (r.rev / totalRev) * 100 : 0 }))
      .sort((a, b) => b.rev - a.rev || b.leads - a.leads);
  }, [data, filteredLeads, filteredInvoices]);

  const splitRows = useMemo(() => {
    if (!data) return [];
    const m = {};
    for (const l of data.leads.filter((x) => passOp(x.owner_name || '(none)', spState))) {
      const o = l.owner_name || '(none)';
      const e = m[o] || (m[o] = { owner: o, assigned: 0, excluded: 0, perStatus: {} });
      e.assigned += 1;
      if (EXCLUDED_STATUSES.includes(l.status)) {
        e.excluded += 1;
        e.perStatus[l.status] = (e.perStatus[l.status] || 0) + 1;
      }
    }
    return Object.values(m).sort((a, b) => b.assigned - a.assigned);
  }, [data, spState]);

  const kpi = useMemo(() => ({
    leads: ownerLeads.length,
    assigned: (data?.leads || []).filter((l) => passOp(l.owner_name || '(none)', spState) && (!owner || l.owner_name === owner)).length,
    enrols: new Set(ownerLinkedInvoices.map((i) => i.enrolment_no)).size,
    linkedInv: ownerLinkedInvoices.length,
    linkedRev: ownerLinkedInvoices.reduce((a, i) => a + i.total, 0),
  }), [data, owner, spState, ownerLeads, ownerLinkedInvoices]);

  const crmRows = useMemo(() => {
    if (!data) return [];
    const m = {};
    for (const l of ownerLeads) {
      const g = l.group || '(none)';
      const e = m[g] || (m[g] = { group: g, leads: 0, statuses: {}, enrolSet: new Set(), inv: 0, rev: 0 });
      e.leads += 1;
      const st = l.status || '(none)';
      e.statuses[st] = (e.statuses[st] || 0) + 1;
    }
    for (const i of ownerLinkedInvoices) {
      const e = m[i.group] || (m[i.group] = { group: i.group, leads: 0, statuses: {}, enrolSet: new Set(), inv: 0, rev: 0 });
      e.enrolSet.add(i.enrolment_no);
      e.inv += 1; e.rev += i.total;
    }
    return Object.values(m).map((r) => ({ ...r, enrols: r.enrolSet.size })).sort((a, b) => b.leads - a.leads || b.rev - a.rev);
  }, [data, ownerLeads, ownerLinkedInvoices]);

  const booksRows = useMemo(() => {
    if (!data) return [];
    const m = {};
    for (const i of filteredBooksInvoices) {
      const e = m[i.category] || (m[i.category] = { category: i.category, inv: 0, rev: 0 });
      e.inv += 1; e.rev += i.total;
    }
    return Object.values(m).sort((a, b) => b.rev - a.rev);
  }, [data]);

  const openCrmDrill = (row) => setDrill({
    open: true, tab: 'leads', stats: null, catRows: [],
    title: `${row.group} · ${range.label}${owner ? ` · ${owner}` : ''}`,
    leads: ownerLeads.filter((l) => (l.group || '(none)') === row.group),
    invoices: ownerLinkedInvoices.filter((i) => i.group === row.group),
  });

  const openOwnerDrill = (row) => {
    const oLeads = filteredLeads.filter((l) => (l.owner_name || '(none)') === row.owner);
    const oInv = filteredInvoices.filter((i) => (i.owner_name || '(none)') === row.owner);
    const m = {};
    for (const l of oLeads) {
      const g = l.group || '(none)';
      const e = m[g] || (m[g] = { group: g, leads: 0, enrolSet: new Set(), inv: 0, rev: 0 });
      e.leads += 1;
    }
    for (const i of oInv) {
      const e = m[i.group] || (m[i.group] = { group: i.group, leads: 0, enrolSet: new Set(), inv: 0, rev: 0 });
      e.enrolSet.add(i.enrolment_no);
      e.inv += 1; e.rev += i.total;
    }
    setDrill({
      open: true, tab: 'leads', catLabel: 'Category',
      title: `${row.owner} · ${range.label}`,
      stats: { leads: row.leads, enrols: row.enrols, convPct: row.convPct, inv: row.inv, rev: row.rev, salesPct: row.salesPct },
      catRows: Object.values(m).map((c) => ({ ...c, enrols: c.enrolSet.size })).sort((a, b) => b.leads - a.leads || b.rev - a.rev),
      leads: oLeads,
      invoices: oInv,
    });
  };

  const openCategoryDrill = (row) => {
    const cLeads = filteredLeads.filter((l) => (l.group || '(none)') === row.owner);
    const cInv = filteredInvoices.filter((i) => (i.group || '(none)') === row.owner);
    const m = {};
    for (const l of cLeads) {
      const o = l.owner_name || '(none)';
      const e = m[o] || (m[o] = { group: o, leads: 0, enrolSet: new Set(), inv: 0, rev: 0 });
      e.leads += 1;
    }
    for (const i of cInv) {
      const o = i.owner_name || '(none)';
      const e = m[o] || (m[o] = { group: o, leads: 0, enrolSet: new Set(), inv: 0, rev: 0 });
      e.enrolSet.add(i.enrolment_no);
      e.inv += 1; e.rev += i.total;
    }
    setDrill({
      open: true, tab: 'leads', catLabel: 'Sales User',
      title: `${row.owner} · ${range.label}`,
      stats: { leads: row.leads, enrols: row.enrols, convPct: row.convPct, inv: row.inv, rev: row.rev, salesPct: row.salesPct },
      catRows: Object.values(m).map((c) => ({ ...c, enrols: c.enrolSet.size })).sort((a, b) => b.leads - a.leads || b.rev - a.rev),
      leads: cLeads,
      invoices: cInv,
    });
  };

  const openBooksDrill = (row) => setDrill({
    open: true, tab: 'invoices', stats: null, catRows: [],
    title: `${row.category} · Books invoices · ${range.label} · ${owner}`,
    leads: [],
    invoices: filteredBooksInvoices.filter((i) => i.category === row.category).map((i) => ({ ...i, books_salesperson: owner, group: i.category })),
  });

  const presets = [['this_month', 'This month'], ['last_month', 'Last month'], ['month', 'Specific month'], ['this_fy', 'This FY'], ['custom', 'Custom']];

  return (
    <div className="space-y-5" data-testid="sales-conversion-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sales Conversion</h1>
          <p className="text-sm text-slate-500 mt-0.5">CRM hot-conversion leads → enrolments → revenue · leads keyed on <b>Date of Hot Conversion</b></p>
        </div>
        <div className="flex items-center gap-2">
          {sync.last_sync && <span className="text-[11px] text-slate-400">{sync.lead_count} leads · {new Date(sync.last_sync).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}
          {/* Unified via the Books Analytics shared pill; kept "Sync all history" as an admin one-off. */}
          <GlobalSyncPill />
          <Button variant="outline" size="sm" onClick={() => startSync(true)} disabled={sync.running} data-testid="lead-sync-all-btn" title="One-time backfill: pulls every hot-conversion lead since Apr 2022 so all historical periods are available to the team">
            Sync all history
          </Button>
          <Button variant="ghost" size="sm" onClick={fetchReport} data-testid="reload-btn"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Reload</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="sc-filter-bar">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Range</span>
        {presets.map(([id, label]) => (
          <button key={id} type="button" onClick={() => setPreset(id)} data-testid={`sc-preset-${id}`}
            className={`text-[11px] px-2.5 py-1 rounded-md border transition ${preset === id ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{label}</button>
        ))}
        {preset === 'month' && (
          <select value={monthKey} onChange={(e) => setMonthKey(e.target.value)} className="text-[11px] border border-slate-200 rounded-md px-2 py-1 bg-white" data-testid="sc-month-select">
            {monthOpts.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        )}
        {preset === 'custom' && (
          <span className="flex items-center gap-1">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="text-[11px] border border-slate-200 rounded-md px-2 py-1" data-testid="sc-custom-from" />
            <span className="text-slate-400 text-xs">→</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="text-[11px] border border-slate-200 rounded-md px-2 py-1" data-testid="sc-custom-to" />
          </span>
        )}
        <span className="text-[11px] text-slate-400">{range.from} → {range.to}</span>
        <button
          type="button"
          onClick={() => setExcludeNonConnect(!excludeNonConnect)}
          title={`Statuses excluded: ${EXCLUDED_STATUSES.join(', ')}`}
          data-testid="sc-exclude-nonconnect-toggle"
          className={`text-[11px] px-2.5 py-1 rounded-md border transition ${excludeNonConnect ? 'bg-emerald-50 border-emerald-200 text-emerald-700 font-medium' : 'bg-amber-50 border-amber-300 text-amber-700 font-medium'}`}>
          {excludeNonConnect ? '✓ Non-connect statuses excluded' : '⚠ Non-connect statuses included'}
        </button>
        <SpFilter all={allOwnerNames} state={spState} setState={setSpState} label="Sales person" testId="sc-sp-filter" />
        <SpFilter all={allClientTypes} state={ctState} setState={setCtState} label="Client type" testId="sc-ct-filter" />
        <span className="ml-auto flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Sales user</span>
          <select value={owner} onChange={(e) => setOwner(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white min-w-[180px]" data-testid="sc-owner-select">
            <option value="">All owners ({data?.owners?.reduce((a, o) => a + o.leads, 0) || 0} leads)</option>
            {(data?.owners || []).map((o) => <option key={o.name} value={o.name}>{o.name} ({o.leads})</option>)}
          </select>
        </span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading…</div>
      ) : !data || data.leads.length === 0 ? (
        <Card className="p-10 text-center text-slate-500 text-sm" data-testid="sc-empty">
          No hot-conversion leads cached for this range. Click <b>Sync from CRM</b> to pull leads for {range.label}.
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sc-kpis">
            {[
              { label: 'Hot Leads (Net)', value: kpi.leads, sub: excludeNonConnect ? `${kpi.assigned} assigned − ${kpi.assigned - kpi.leads} non-connect` : 'all assigned included', icon: Users, tint: 'text-violet-600 bg-violet-50' },
              { label: 'Admissions (Enrol No)', value: kpi.enrols, sub: 'invoiced in range', icon: GraduationCap, tint: 'text-emerald-600 bg-emerald-50' },
              { label: 'Lead-linked Revenue', value: fmtInr(kpi.linkedRev), sub: `${kpi.linkedInv} invoices`, icon: IndianRupee, tint: 'text-amber-600 bg-amber-50' },
              { label: 'Books Invoices', value: owner ? s.books_invoice_count : '—', sub: owner ? 'salesperson attribution' : 'select a sales user', icon: FileText, tint: 'text-sky-600 bg-sky-50' },
              { label: 'Books Revenue', value: owner ? fmtInr(s.books_revenue) : '—', sub: owner ? (data.matched_books_salespersons.join(', ') || 'no Books match') : '', icon: IndianRupee, tint: 'text-rose-600 bg-rose-50' },
            ].map((k) => (
              <Card key={k.label} className="p-4" data-testid={`sc-kpi-${k.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${k.tint}`}><k.icon className="w-4 h-4" /></div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{k.label}</div>
                <div className="text-xl font-bold text-slate-900 mt-0.5">{k.value}</div>
                {k.sub && <div className="text-[11px] text-slate-400 mt-0.5 truncate">{k.sub}</div>}
              </Card>
            ))}
          </div>

          <Card className="overflow-hidden" data-testid="sc-perf-table">
            <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-2 flex-wrap">
              <div>
                <h2 className="text-sm font-semibold text-slate-800">{perfView === 'user' ? 'Sales User Performance — all users' : 'Category Performance'}</h2>
                <p className="text-[11px] text-slate-400">Net hot leads · lead→admission conversion % · share of total lead-linked revenue · click a row for the full breakdown</p>
              </div>
              <span className="flex items-center gap-1">
                {[['user', 'By sales user'], ['category', 'By category']].map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setPerfView(v)} data-testid={`sc-perf-view-${v}`}
                    className={`text-[11px] px-2.5 py-1 rounded-md border transition ${perfView === v ? 'bg-violet-600 text-white border-violet-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{label}</button>
                ))}
              </span>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="text-left px-4 py-2">{perfView === 'user' ? 'Sales User' : 'Category'}</th>
                <th className="text-right px-3 py-2">Net Hot Leads</th>
                <th className="text-right px-3 py-2">Admissions</th>
                <th className="text-right px-3 py-2">Conversion %</th>
                <th className="text-right px-3 py-2">Invoice Count</th>
                <th className="text-right px-3 py-2">Revenue</th>
                <th className="text-right px-4 py-2">% of Total Sales</th>
              </tr></thead>
              <tbody>
                {(perfView === 'user' ? perfRows : perfCatRows).map((r) => (
                  <tr key={r.owner} className={`border-b border-slate-50 hover:bg-violet-50/40 cursor-pointer ${perfView === 'user' && owner && r.owner === owner ? 'bg-violet-50/60' : ''}`} onClick={() => perfView === 'user' ? openOwnerDrill(r) : openCategoryDrill(r)} data-testid={`sc-perf-row-${r.owner}`}>
                    <td className="px-4 py-2.5 font-medium text-slate-800">{r.owner}</td>
                    <td className="px-3 py-2.5 text-right font-semibold">{r.leads}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-600 font-semibold">{r.enrols}</td>
                    <td className="px-3 py-2.5 text-right">
                      <span className={`inline-block min-w-[52px] text-center px-1.5 py-0.5 rounded-md text-xs font-semibold ${r.convPct >= 8 ? 'bg-emerald-100 text-emerald-700' : r.convPct >= 4 ? 'bg-amber-100 text-amber-700' : 'bg-rose-50 text-rose-600'}`}>{r.convPct.toFixed(1)}%</span>
                    </td>
                    <td className="px-3 py-2.5 text-right">{r.inv}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-slate-900">{fmtInr(r.rev)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="inline-flex items-center gap-1.5">
                        <span className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden"><span className="block h-full bg-violet-500 rounded-full" style={{ width: `${Math.min(r.salesPct, 100)}%` }} /></span>
                        <span className="font-semibold text-slate-700 w-12 text-right">{r.salesPct.toFixed(1)}%</span>
                      </span>
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-bold text-slate-900">
                  <td className="px-4 py-2.5">TOTAL</td>
                  <td className="px-3 py-2.5 text-right">{perfRows.reduce((a, r) => a + r.leads, 0)}</td>
                  <td className="px-3 py-2.5 text-right text-emerald-700">{perfRows.reduce((a, r) => a + r.enrols, 0)}</td>
                  <td className="px-3 py-2.5 text-right">{(() => { const l = perfRows.reduce((a, r) => a + r.leads, 0); const e = perfRows.reduce((a, r) => a + r.enrols, 0); return l ? `${((e / l) * 100).toFixed(1)}%` : '—'; })()}</td>
                  <td className="px-3 py-2.5 text-right">{perfRows.reduce((a, r) => a + r.inv, 0)}</td>
                  <td className="px-3 py-2.5 text-right">{fmtInr(perfRows.reduce((a, r) => a + r.rev, 0))}</td>
                  <td className="px-4 py-2.5 text-right">100%</td>
                </tr>
              </tbody>
            </table>
          </Card>

          <Card className="overflow-hidden" data-testid="sc-split-table">
            <div className="px-4 pt-4 pb-2">
              <h2 className="text-sm font-semibold text-slate-800">Non-connect Split — assigned vs net hot leads</h2>
              <p className="text-[11px] text-slate-400">Leads marked by the sales user as non-connect ({EXCLUDED_STATUSES.join(' · ')}) are removed from the report by default</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="text-left px-4 py-2">Sales User</th>
                  <th className="text-right px-3 py-2">Assigned</th>
                  {EXCLUDED_STATUSES.map((st) => <th key={st} className="text-right px-3 py-2 whitespace-nowrap">{st}</th>)}
                  <th className="text-right px-3 py-2">Excluded</th>
                  <th className="text-right px-4 py-2">Net Hot Leads</th>
                </tr></thead>
                <tbody>
                  {splitRows.map((r) => (
                    <tr key={r.owner} className={`border-b border-slate-50 ${owner && r.owner === owner ? 'bg-violet-50/40' : ''}`} data-testid={`sc-split-row-${r.owner}`}>
                      <td className="px-4 py-2 font-medium text-slate-800">{r.owner}</td>
                      <td className="px-3 py-2 text-right">{r.assigned}</td>
                      {EXCLUDED_STATUSES.map((st) => <td key={st} className="px-3 py-2 text-right text-slate-500">{r.perStatus[st] || '—'}</td>)}
                      <td className="px-3 py-2 text-right font-semibold text-rose-600">−{r.excluded}</td>
                      <td className="px-4 py-2 text-right font-bold text-slate-900">{r.assigned - r.excluded}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50 font-bold text-slate-900">
                    <td className="px-4 py-2">TOTAL</td>
                    <td className="px-3 py-2 text-right">{splitRows.reduce((a, r) => a + r.assigned, 0)}</td>
                    {EXCLUDED_STATUSES.map((st) => <td key={st} className="px-3 py-2 text-right">{splitRows.reduce((a, r) => a + (r.perStatus[st] || 0), 0)}</td>)}
                    <td className="px-3 py-2 text-right text-rose-700">−{splitRows.reduce((a, r) => a + r.excluded, 0)}</td>
                    <td className="px-4 py-2 text-right">{splitRows.reduce((a, r) => a + r.assigned - r.excluded, 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="overflow-hidden" data-testid="sc-crm-table">
            <div className="px-4 pt-4 pb-2">
              <h2 className="text-sm font-semibold text-slate-800">Lead Attribution (CRM){owner ? ` — ${owner}` : ' — all owners'}</h2>
              <p className="text-[11px] text-slate-400">Rows = CRM Group Name · admissions & revenue from invoices <b>dated within the range</b> (linked via Enrolment No → lead owner, else Books salesperson) · click a row to drill</p>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="text-left px-4 py-2">Category</th><th className="text-right px-3 py-2">Leads</th>
                <th className="text-left px-3 py-2">Lead Status</th>
                <th className="text-right px-3 py-2">Enrolments</th><th className="text-right px-3 py-2">Invoice Count</th><th className="text-right px-4 py-2">Revenue</th>
              </tr></thead>
              <tbody>
                {crmRows.map((r) => (
                  <tr key={r.group} className="border-b border-slate-50 hover:bg-violet-50/40 cursor-pointer" onClick={() => openCrmDrill(r)} data-testid={`sc-crm-row-${r.group}`}>
                    <td className="px-4 py-2.5 font-medium text-slate-800">{r.group}</td>
                    <td className="px-3 py-2.5 text-right font-semibold">{r.leads}</td>
                    <td className="px-3 py-2.5">
                      <span className="flex flex-wrap gap-1">
                        {Object.entries(r.statuses).sort((a, b) => b[1] - a[1]).map(([st, c]) => (
                          <Badge key={st} variant="secondary" className={`text-[10px] px-1.5 py-0 font-medium ${STATUS_COLORS[st] || 'bg-slate-100 text-slate-500'}`}>{st} {c}</Badge>
                        ))}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-emerald-600 font-semibold">{r.enrols}</td>
                    <td className="px-3 py-2.5 text-right">{r.inv}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{fmtInr(r.rev)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-bold text-slate-900">
                  <td className="px-4 py-2.5">TOTAL</td>
                  <td className="px-3 py-2.5 text-right">{crmRows.reduce((a, r) => a + r.leads, 0)}</td>
                  <td />
                  <td className="px-3 py-2.5 text-right text-emerald-700">{crmRows.reduce((a, r) => a + r.enrols, 0)}</td>
                  <td className="px-3 py-2.5 text-right">{crmRows.reduce((a, r) => a + r.inv, 0)}</td>
                  <td className="px-4 py-2.5 text-right">{fmtInr(crmRows.reduce((a, r) => a + r.rev, 0))}</td>
                </tr>
              </tbody>
            </table>
          </Card>

          <Card className="overflow-hidden" data-testid="sc-books-table">
            <div className="px-4 pt-4 pb-2">
              <h2 className="text-sm font-semibold text-slate-800">Books Attribution (salesperson){owner ? ` — ${owner}` : ''}</h2>
              <p className="text-[11px] text-slate-400">Rows = Books category · invoices dated within the range where salesperson ≈ selected user · click a row to drill</p>
            </div>
            {!owner ? (
              <div className="px-4 pb-6 text-sm text-slate-400">Select a sales user above to see their Books-side invoices.</div>
            ) : booksRows.length === 0 ? (
              <div className="px-4 pb-6 text-sm text-slate-400">No Books invoices found for “{owner}” in this range.</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="text-left px-4 py-2">Category (Books)</th><th className="text-right px-3 py-2">Invoice Count</th><th className="text-right px-4 py-2">Revenue</th>
                </tr></thead>
                <tbody>
                  {booksRows.map((r) => (
                    <tr key={r.category} className="border-b border-slate-50 hover:bg-sky-50/40 cursor-pointer" onClick={() => openBooksDrill(r)} data-testid={`sc-books-row-${r.category}`}>
                      <td className="px-4 py-2.5 font-medium text-slate-800">{r.category}</td>
                      <td className="px-3 py-2.5 text-right">{r.inv}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{fmtInr(r.rev)}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50 font-bold text-slate-900">
                    <td className="px-4 py-2.5">TOTAL</td>
                    <td className="px-3 py-2.5 text-right">{booksRows.reduce((a, r) => a + r.inv, 0)}</td>
                    <td className="px-4 py-2.5 text-right">{fmtInr(booksRows.reduce((a, r) => a + r.rev, 0))}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}

      <Dialog open={drill.open} onOpenChange={(o) => setDrill((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="sc-drill-dialog">
          <DialogHeader><DialogTitle className="text-base">{drill.title}</DialogTitle></DialogHeader>
          {drill.stats && (
            <>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="sc-owner-drill-stats">
                {[
                  ['Net Hot Leads', drill.stats.leads],
                  ['Admissions', drill.stats.enrols],
                  ['Conversion %', `${drill.stats.convPct.toFixed(1)}%`],
                  ['Invoices', drill.stats.inv],
                  ['Revenue', fmtInr(drill.stats.rev)],
                  ['% of Total Sales', `${drill.stats.salesPct.toFixed(1)}%`],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border border-slate-100 bg-slate-50/60 px-2.5 py-2">
                    <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
                    <div className="text-sm font-bold text-slate-900 mt-0.5">{value}</div>
                  </div>
                ))}
              </div>
              <table className="w-full text-xs" data-testid="sc-owner-drill-cats">
                <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="text-left px-2 py-1.5">{drill.catLabel || 'Category'}</th><th className="text-right px-2 py-1.5">Leads</th>
                  <th className="text-right px-2 py-1.5">Admissions</th><th className="text-right px-2 py-1.5">Invoice Count</th><th className="text-right px-2 py-1.5">Revenue</th>
                </tr></thead>
                <tbody>
                  {drill.catRows.map((c) => (
                    <tr key={c.group} className="border-b border-slate-50">
                      <td className="px-2 py-1.5 font-medium text-slate-800">{c.group}</td>
                      <td className="px-2 py-1.5 text-right">{c.leads}</td>
                      <td className="px-2 py-1.5 text-right text-emerald-600 font-semibold">{c.enrols}</td>
                      <td className="px-2 py-1.5 text-right">{c.inv}</td>
                      <td className="px-2 py-1.5 text-right font-semibold">{fmtInr(c.rev)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          <Tabs value={drill.tab} onValueChange={(t) => setDrill((d) => ({ ...d, tab: t }))}>
            <TabsList className="h-8">
              <TabsTrigger value="leads" className="text-xs" disabled={drill.leads.length === 0} data-testid="sc-drill-tab-leads">Leads ({drill.leads.length})</TabsTrigger>
              <TabsTrigger value="invoices" className="text-xs" data-testid="sc-drill-tab-invoices">Invoices ({drill.invoices.length})</TabsTrigger>
            </TabsList>
          </Tabs>
          {drill.tab === 'leads' ? (
            <table className="w-full text-xs">
              <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="text-left px-2 py-1.5">Lead</th><th className="text-left px-2 py-1.5">Owner</th><th className="text-left px-2 py-1.5">Status</th>
                <th className="text-left px-2 py-1.5">Hot Conversion</th><th className="text-left px-2 py-1.5">Enrol No</th><th className="px-2 py-1.5" />
              </tr></thead>
              <tbody>
                {drill.leads.map((l) => (
                  <tr key={l.lead_id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-2 py-1.5 font-medium text-slate-800">{l.name}</td>
                    <td className="px-2 py-1.5 text-slate-500">{l.owner_name}</td>
                    <td className="px-2 py-1.5"><Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${STATUS_COLORS[l.status] || 'bg-slate-100 text-slate-500'}`}>{l.status || '—'}</Badge></td>
                    <td className="px-2 py-1.5 text-slate-500">{l.hot_datetime ? new Date(l.hot_datetime).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : l.hot_date}</td>
                    <td className="px-2 py-1.5 font-mono">{l.enrolment_no || '—'}</td>
                    <td className="px-2 py-1.5"><a href={crmLeadUrl(l.lead_id)} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>Open<ExternalLink className="w-3 h-3" /></a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-xs">
              <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="text-left px-2 py-1.5">Invoice #</th><th className="text-left px-2 py-1.5">Customer</th><th className="text-left px-2 py-1.5">Date</th>
                <th className="text-right px-2 py-1.5">Total</th><th className="text-left px-2 py-1.5">Status</th><th className="text-left px-2 py-1.5">Books SP</th>
                <th className="text-left px-2 py-1.5">Enrol No</th><th className="px-2 py-1.5" />
              </tr></thead>
              <tbody>
                {drill.invoices.map((i) => (
                  <tr key={i.invoice_id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-2 py-1.5 font-mono text-slate-700">{i.invoice_number}</td>
                    <td className="px-2 py-1.5 font-medium text-slate-800">{i.customer_name}</td>
                    <td className="px-2 py-1.5 text-slate-500">{i.date}</td>
                    <td className="px-2 py-1.5 text-right font-semibold">{fmtInr(i.total)}</td>
                    <td className="px-2 py-1.5"><Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${i.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : i.status === 'overdue' ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-500'}`}>{i.status}</Badge></td>
                    <td className="px-2 py-1.5 text-slate-500">{i.books_salesperson}</td>
                    <td className="px-2 py-1.5 font-mono">{i.enrolment_no || '—'}</td>
                    <td className="px-2 py-1.5"><a href={zohoInvUrl(i.invoice_id)} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>Open<ExternalLink className="w-3 h-3" /></a></td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-bold">
                  <td className="px-2 py-1.5" colSpan={3}>TOTAL</td>
                  <td className="px-2 py-1.5 text-right">{fmtInr(drill.invoices.reduce((a, i) => a + i.total, 0))}</td>
                  <td colSpan={4} />
                </tr>
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
